import { ContractState, ContractViewModel } from '@ail/contracts-api'
import { VehicleOrderViewModel } from '@ail/orders-api'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Arr } from '../../../helpers/Arr'
import { PermissionManager } from '../../../helpers/PermissionManager'
import { formatNumberToLocale, printDate } from '../../../helpers/Utils'
import { useAuth } from '../../../hooks/useAuth'
import { activeServicesIconsDict } from '../../../types/constants'
import { VehicleNatureIcons } from '../../../utils/contracts'
import ProgressCircle2 from '../../common/ProgressCircle2/ProgressCircle2'
import { useIsInterfleetPro } from '../../../hooks/useIsInterfleetPro'

export function DurationAndMileageDetails(props: {
  contract?: ContractViewModel
  vehicleOrder?: VehicleOrderViewModel
  expectedDistance: number
  actualDistance: number
  expectedCosts: number
  actualCosts: number
  excludeRestricted: boolean
  show: boolean
}) {
  const [showMileages, setShowMileages] = useState(false)
  const [showMileagesTooltip, setShowMileagesTooltip] = useState(false)
  const { user } = useAuth()
  const isInterfleetPro = useIsInterfleetPro()
  const { t } = useTranslation('common')

  useEffect(() => {
    setShowMileages(props.show)
  }, [props.show])

  const contractProgress = useMemo(() => {
    if (!props.contract?.duration) {
      return { elapsed: 0, total: 0, percentage: 100 }
    }

    if (props.contract.state === ContractState.Initialized) {
      return { elapsed: 0, total: props.contract.duration, percentage: 0 }
    }

    const now = new Date()
    const startDate = new Date(props.contract.startDate)
    const endDate = props.contract.endDate ? new Date(props.contract.endDate) : undefined

    if (startDate.getTime() === -5364668194000 || endDate?.getTime() === 32535122400000) {
      return { elapsed: 0, total: 0, percentage: 0 }
    }

    if (endDate && endDate > now) {
      const elapsed = (now.getTime() - startDate.getTime()) / 1000 / 3600 / 24 / 30
      const percentage = (elapsed / props.contract.duration) * 100
      return {
        elapsed,
        total: props.contract.duration,
        percentage: Math.max(Math.min(percentage, 100), 0),
      }
    }

    if (endDate && endDate <= now) {
      const elapsed = (endDate.getTime() - startDate.getTime()) / 1000 / 3600 / 24 / 30
      const percentage = (elapsed / props.contract.duration) * 100
      return {
        elapsed,
        total: props.contract.duration,
        percentage: Math.max(Math.min(percentage, 100), 0),
      }
    }

    return { elapsed: 0, total: 0, percentage: 0 }
  }, [props.contract])

  const sortedMileages = useMemo(() => {
    return Arr.sort(props.contract?.vehicle?.mileages ?? [], (m) => new Date(m.eventDate), 'DESC')
  }, [props.contract])

  const mileageIssues = useMemo(() => {
    return sortedMileages.filter((m, i) => {
      const previousMileage = sortedMileages.at(i + 1)
      return (
        !m.distance ||
        m.distance === 1 ||
        m.distance === 11 ||
        (previousMileage && previousMileage.distance > m.distance)
      )
    })
  }, [sortedMileages])

  const lastMileage = useMemo(() => {
    const validMileages = sortedMileages.filter((m) => m.distance && m.distance !== 1 && m.distance !== 11)
    return validMileages.at(0)
  }, [sortedMileages])

  const dateIncorrect =
    props.contract?.startDate.includes('1800') ||
    props.contract?.endDate.includes('3000') ||
    props.contract?.state === ContractState.Initialized

  const vehicleNature = props.contract?.vehicle?.nature
  const vehicleNatureIcon = vehicleNature ? VehicleNatureIcons[vehicleNature] : undefined
  return (
    <div className="detailContentArea" style={{ zIndex: 1 }}>
      <div className="detailTitle between">
        <span>
          <i className="icon-clock" /> <span>{t('DURATION_AND_MILEAGE')}</span>
        </span>
        <i
          className={`orderDetailToggle ${showMileages ? 'icon-up' : 'icon-bottom'}`}
          onClick={() => {
            setShowMileages(!showMileages)
          }}
        />
      </div>
      {showMileages && (
        <div className="details mileages">
          <div className="flex duration">
            <i className="icon-go" />
            <i className="icon-flag" />
          </div>
          <div className="carStreet">
            <i className={vehicleNatureIcon} style={{ left: `${contractProgress.percentage}%` }} />
          </div>
          <div className="durationBar">
            <span style={{ transform: `scaleX(${contractProgress.percentage / 100})` }} />
          </div>
          <div className="flex durationDates">
            <span>{dateIncorrect ? '' : `${printDate(props.contract?.startDate)}`}</span>
            <span>
              {Math.round(contractProgress.elapsed)}/{contractProgress.total}
            </span>
            <span>{dateIncorrect ? '' : `${printDate(props.contract?.endDate)}`}</span>
          </div>
          <div className="extra-data">
            <span>
              {props.vehicleOrder?.deliveryAtCustomerDate && (
                <>{printDate(props.vehicleOrder.deliveryAtCustomerDate)}</>
              )}
            </span>
            <span>
              {props.contract?.imposedReturnDate &&
                (user?.isInternalUser || isInterfleetPro) &&
                !props.excludeRestricted && <>{printDate(props.contract?.imposedReturnDate)}</>}
            </span>
          </div>
          {props.contract?.endIntention && user?.isInternalUser && !props.excludeRestricted && (
            <div className="endIntention">
              <span>
                {t('END_INTENTION') as string}
                {`: ${t(props.contract.endIntention.toUpperCase())}`}
              </span>
            </div>
          )}
          {PermissionManager.canViewCosts(user!) && props.contract?.state !== ContractState.Closed && (
            <div className="mileageArea">
              <div className="milestats">
                <div className={`duration-pie`} style={{ display: 'flex', alignItems: 'center' }}>
                  <ProgressCircle2
                    progress={
                      props.actualDistance > props.expectedDistance ? props.expectedDistance : props.actualDistance
                    }
                    customText={formatNumberToLocale(props.actualDistance, 0, 0)}
                    color={props.actualDistance > props.expectedDistance ? 'red' : 'green'}
                    maxValue={props.expectedDistance}
                    innerDetails={t('KM')}
                    details1={formatNumberToLocale(props.actualDistance, 0, 0)}
                    details2={formatNumberToLocale(props.expectedDistance, 0, 0)}
                  />
                </div>
              </div>
              <div className="milestats">
                <div className={`duration-pie`} style={{ display: 'flex', alignItems: 'center' }}>
                  <ProgressCircle2
                    progress={props.actualCosts > props.expectedCosts ? props.expectedCosts : props.actualCosts}
                    customText={formatNumberToLocale(Math.round(props.actualCosts), 0, 0)}
                    color={props.actualCosts > props.expectedCosts ? 'red' : 'green'}
                    maxValue={props.expectedCosts}
                    innerDetails={t('CHF')}
                    details1={formatNumberToLocale(props.actualCosts, 0, 0)}
                    details2={formatNumberToLocale(props.expectedCosts, 0, 0)}
                  />
                </div>
              </div>
              <div className="milestats"></div>
            </div>
          )}
          {!!lastMileage && (
            <div className="mileage">
              <i className="icon-kilometers"></i>
              {formatNumberToLocale(lastMileage.distance || 0, 0, 0)} Km
              <div className="mileDate">{`(${printDate(lastMileage.eventDate)})`}</div>
              <i
                onClick={() => setShowMileagesTooltip(!showMileagesTooltip)}
                className={mileageIssues.length ? 'icon-warning' : 'icon-info'}
              ></i>
              {showMileagesTooltip && (
                <div className="tooltip">
                  <ul className="lastMile">
                    {sortedMileages.map((el) => (
                      <li>
                        <div className="flex">
                          <div className="dataMile">{printDate(el.eventDate)}</div>
                          <div className="typeMile">
                            <i className={activeServicesIconsDict[el.serviceKind] || 'icon-4u'}></i>
                            {typeof el.serviceKind === 'string' && t(el.serviceKind?.toUpperCase())}
                          </div>
                          <div className="kmMile">
                            {mileageIssues.includes(el) && <i className="icon-warning"></i>}
                            {formatNumberToLocale((el.distance as number) || 0, 0, 0)}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
